import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material';

import axios from 'axios';
import { useSelector } from 'react-redux';

const OrderDetailComp = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { dealerInfo } = useSelector((state) => state.dealerLogin);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${dealerInfo.token}`,
            'Content-Type': 'application/json',
          },
        };
        const { data } = await axios.get(
          `${process.env.REACT_APP_DP_API}/api/dealer-portal/orders/${id}`,
          config
        );
        setOrder(data.order || null);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load order details');
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, dealerInfo.token]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error || 'No order data available'}
        </Alert>
      </Box>
    );
  }

  const statusLabels = {
    ready_to_process: 'Ordered',
    processing: 'Processing',
    processed: 'Ready to Pick Up',
    delivered: 'Picked Up',
    Ordered: 'Ordered',
    new: 'Ordered',
    ordered: 'Ordered',
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Ordered': 'primary',
      'Processing': 'warning',
      'Ready to Pick Up': 'success',
      'Picked Up': 'default',
    };
    return statusMap[statusLabels[status]] || 'default';
  };

  const generateTableHeaders = (orderArray) => {
    if (!orderArray || orderArray.length === 0) return [];

    const mainFields = [
      'width',
      'height',
      'headrail',
      'sleeve',
      'productMaterial',
      'liftStyle',
      'factor',
    ];
    const optionalFields = [
      'control',
      'location',
      'lift',
      'remark',
      'tile',
      'deduction',
      'addition',
    ];

    let extraFieldKeys = [];
    orderArray.forEach((item) => {
      if (item.extraFields) {
        Object.keys(item.extraFields).forEach((key) => {
          if (!extraFieldKeys.includes(key)) {
            extraFieldKeys.push(key);
          }
        });
      }
    });

    const validOptionalFields = optionalFields.filter((field) =>
      orderArray.some((item) => item[field] && item[field].toString().trim() !== '')
    );

    return [...mainFields, ...validOptionalFields, ...extraFieldKeys].filter(
      (key) => !['_id', 'id', 'extraFields', 'productHeadrail'].includes(key)
    );
  };

  const formatHeader = (header) => {
    return header
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const headers = generateTableHeaders(order.orderArray);

  return (
    <Box sx={{ 
      maxWidth: 1400, 
      mx: 'auto', 
      mt: 3, 
      mb: 4, 
      px: { xs: 2, sm: 3 } 
    }}>
      {/* Header Section */}
      <Paper elevation={1} sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 2,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: 2 
        }}>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 700, 
              mb: 1,
              color: 'text.primary'
            }}>
              Order #{order.orderNumber || 'N/A'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {order.createdAt
                ? new Date(order.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </Typography>
          </Box>
          <Chip
            label={statusLabels[order.orderStatus] || order.orderStatus || 'Unknown'}
            color={getStatusColor(order.orderStatus)}
            sx={{ 
              px: 2, 
              py: 2.5, 
              fontSize: '0.9rem', 
              fontWeight: 600,
              borderRadius: 1
            }}
          />
        </Box>
      </Paper>

      {/* Order Information Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card elevation={1} sx={{ 
            height: '100%', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ 
                mb: 2, 
                fontWeight: 600,
                color: 'text.primary'
              }}>
                Order Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Dealer Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {order.dealerName || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Product Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {order.productType || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                   <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Order Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {order.orderNumber || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Tag
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {order.tag || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Order Factor (SQFT)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {order.orderFactor || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {order.remark && order.remark !== 'None' && (
          <Grid item xs={12} md={4}>
            <Card elevation={1} sx={{ 
              height: '100%', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <CardContent sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: 'text.primary'
                }}>
                  Remarks
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    overflowWrap: 'break-word'
                  }}
                >
                  {order.remark}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Order Items Table */}
      <Paper elevation={1} sx={{ 
        borderRadius: 2, 
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ 
          p: 3, 
          pb: 2,
          backgroundColor: 'grey.50'
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600,
            color: 'text.primary'
          }}>
            Order Items ({order.orderArray?.length || 0})
          </Typography>
        </Box>
        <Divider />
        {order.orderArray && order.orderArray.length > 0 ? (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 700, 
                    backgroundColor: 'grey.100',
                    color: 'text.primary',
                    fontSize: '0.9rem'
                  }}>
                    #
                  </TableCell>
                  {headers.map((header) => (
                    <TableCell 
                      key={header} 
                      sx={{ 
                        fontWeight: 700, 
                        backgroundColor: 'grey.100',
                        color: 'text.primary',
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {formatHeader(header)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {order.orderArray.map((item, index) => (
                  <TableRow 
                    key={item._id || index}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'action.hover',
                        transition: 'background-color 0.2s ease'
                      },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell sx={{ 
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                      {index + 1}
                    </TableCell>
                    {headers.map((header) => {
                      let value = '';

                      if (header === 'headrail') {
                        value = item.productHeadrail?.itemId?.name || item.productHeadrail?.itemId || 'N/A';
                      } else if (header === 'sleeve') {
                        value = item.productHeadrail?.sleeve || 'N/A';
                      } else if (header === 'productMaterial' || header === 'liftStyle') {
                        value =
                          typeof item[header] === 'object' && item[header]?.name
                            ? item[header].name
                            : item[header] || 'N/A';
                      } else if (item.extraFields && header in item.extraFields) {
                        value = item.extraFields[header] || 'N/A';
                      } else {
                        value = item[header] || 'N/A';
                      }

                      return (
                        <TableCell 
                          key={header} 
                          sx={{ 
                            whiteSpace: 'nowrap',
                            color: 'text.primary'
                          }}
                        >
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            backgroundColor: 'grey.50'
          }}>
            <Typography color="text.secondary" variant="body1">
              No order items available
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OrderDetailComp;